import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Check } from 'lucide-react';
import { getBackendUrl } from '../config/api';
import { trackEvent } from '../utils/analytics';

// ── Wheel of Times — ornate astronomical clock button ───────────────────────
// Ported from the unused ChakraRewardWidget.jsx. Adds atRisk/broken color
// variants on top of the original claimed/unclaimed treatment.

const WHEEL_VARIANT_COLORS = {
  default: { stroke: 'var(--gold-primary)', glowRgb: '201,168,76', grad2: '#6B21A8' },
  atRisk: { stroke: '#EF4444', glowRgb: '239,68,68', grad2: '#7C2D12' },
  broken: { stroke: '#9333EA', glowRgb: '147,51,234', grad2: '#3B0764' },
};

const WheelOfTimes = ({ claimed, onClick, size = 40, variant = 'default', pulse = true }) => {
  const cx = size / 2;
  const cy = size / 2;
  const R = size / 2 - 1;
  const colors = WHEEL_VARIANT_COLORS[variant] || WHEEL_VARIANT_COLORS.default;
  const gradId = `wot-rg-${size}-${variant}`;
  const glowSpeed = variant === 'atRisk' ? '0.6s' : '2.2s';

  return (
    <button
      onClick={onClick}
      title={claimed ? "View today's Karma Wheel" : 'Spin the Karma Wheel'}
      data-testid="karma-wheel-btn"
      style={{
        width: size,
        height: size,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        filter:
          claimed && variant === 'default'
            ? `drop-shadow(0 0 4px rgba(${colors.glowRgb},0.25))`
            : `drop-shadow(0 0 8px rgba(${colors.glowRgb},0.65)) drop-shadow(0 0 20px rgba(${colors.glowRgb},0.3))`,
        transition: 'filter 0.4s ease',
        opacity: claimed && variant === 'default' ? 0.62 : 1,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
        <defs>
          <style>{`
            @keyframes wot-cw-${variant}   { from { transform-origin: ${cx}px ${cy}px; transform: rotate(0deg); }    to { transform-origin: ${cx}px ${cy}px; transform: rotate(360deg);  } }
            @keyframes wot-ccw-${variant}  { from { transform-origin: ${cx}px ${cy}px; transform: rotate(0deg); }    to { transform-origin: ${cx}px ${cy}px; transform: rotate(-360deg); } }
            @keyframes wot-pulse-${variant}{ 0%,100%{opacity:0.6;} 50%{opacity:1;} }
            .wot-l1-${variant}  { animation: wot-cw-${variant}  22s linear infinite; }
            .wot-l2-${variant}  { animation: wot-ccw-${variant} 14s linear infinite; }
            .wot-l3-${variant}  { animation: wot-cw-${variant}  8s  linear infinite; }
            .wot-glo-${variant} { animation: ${pulse ? `wot-pulse-${variant} ${glowSpeed} ease-in-out infinite` : 'none'}; }
          `}</style>
          <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={colors.stroke} stopOpacity="0.35" />
            <stop offset="100%" stopColor={colors.grad2} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Dark backing disc */}
        <circle cx={cx} cy={cy} r={R} fill="rgba(12,6,28,0.9)" />
        <circle cx={cx} cy={cy} r={R} fill={`url(#${gradId})`} className={`wot-glo-${variant}`} />

        {/* Outer bezel — 24 hour tick marks */}
        <g className={`wot-l1-${variant}`}>
          {Array.from({ length: 24 }, (_, i) => {
            const a = (i / 24) * 2 * Math.PI - Math.PI / 2;
            const isMajor = i % 6 === 0;
            const r1 = R - 1;
            const r2 = R - (isMajor ? 4.5 : 2.5);
            return (
              <line
                key={i}
                x1={+(cx + r1 * Math.cos(a)).toFixed(2)}
                y1={+(cy + r1 * Math.sin(a)).toFixed(2)}
                x2={+(cx + r2 * Math.cos(a)).toFixed(2)}
                y2={+(cy + r2 * Math.sin(a)).toFixed(2)}
                stroke={isMajor ? colors.stroke : `rgba(${colors.glowRgb},0.4)`}
                strokeWidth={isMajor ? 1.2 : 0.6}
              />
            );
          })}
          {/* Outer rim */}
          <circle cx={cx} cy={cy} r={R - 1} fill="none" stroke={colors.stroke} strokeWidth="0.7" opacity="0.55" />
        </g>

        {/* Mid ring — 8 ornate petals */}
        <g
          className={`wot-l2-${variant}`}
          style={{ animation: `wot-ccw-${variant} 14s linear infinite`, transformOrigin: `${cx}px ${cy}px` }}
        >
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * 2 * Math.PI - Math.PI / 2;
            const pr = R * 0.62;
            const pcx = +(cx + pr * Math.cos(a)).toFixed(2);
            const pcy = +(cy + pr * Math.sin(a)).toFixed(2);
            const deg = (i / 8) * 360;
            return (
              <ellipse
                key={i}
                cx={pcx}
                cy={pcy}
                rx={size * 0.1}
                ry={size * 0.045}
                fill={`rgba(${colors.glowRgb},0.18)`}
                stroke={colors.stroke}
                strokeWidth="0.5"
                transform={`rotate(${deg}, ${pcx}, ${pcy})`}
              />
            );
          })}
          <circle cx={cx} cy={cy} r={R * 0.52} fill="none" stroke={`rgba(${colors.glowRgb},0.3)`} strokeWidth="0.5" />
        </g>

        {/* Spoke layer — 8 spokes */}
        <g className={`wot-l3-${variant}`}>
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * 2 * Math.PI;
            return (
              <line
                key={i}
                x1={+(cx + R * 0.26 * Math.cos(a)).toFixed(2)}
                y1={+(cy + R * 0.26 * Math.sin(a)).toFixed(2)}
                x2={+(cx + R * 0.5 * Math.cos(a)).toFixed(2)}
                y2={+(cy + R * 0.5 * Math.sin(a)).toFixed(2)}
                stroke={colors.stroke}
                strokeWidth="0.7"
                opacity="0.6"
              />
            );
          })}
          <circle cx={cx} cy={cy} r={R * 0.27} fill="none" stroke={`rgba(${colors.glowRgb},0.45)`} strokeWidth="0.7" />
        </g>

        {/* Inner lotus ring — 12 small diamond points */}
        <g className={`wot-l3-${variant}`}>
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
            const dr = R * 0.24;
            return (
              <circle
                key={i}
                cx={+(cx + dr * Math.cos(a)).toFixed(2)}
                cy={+(cy + dr * Math.sin(a)).toFixed(2)}
                r={i % 3 === 0 ? 1.4 : 0.8}
                fill={i % 3 === 0 ? colors.stroke : `rgba(${colors.glowRgb},0.5)`}
              />
            );
          })}
        </g>

        {/* Center bindu — radiant core */}
        <circle cx={cx} cy={cy} r={R * 0.13} fill={`rgba(${colors.glowRgb},0.15)`} className={`wot-glo-${variant}`} />
        <circle cx={cx} cy={cy} r={R * 0.08} fill={colors.stroke} opacity="0.95" />
        <circle cx={cx} cy={cy} r={R * 0.04} fill="#FFF8DC" />

        {/* Unclaimed / at-risk pulse ring */}
        {!claimed && (
          <circle
            cx={cx}
            cy={cy}
            r={R * 0.92}
            fill="none"
            stroke={`rgba(${colors.glowRgb},0.5)`}
            strokeWidth="1"
            className={`wot-glo-${variant}`}
          />
        )}
      </svg>

      {/* Unclaimed badge dot — visible even if the pulse alone is missed in a dense header */}
      {!claimed && (
        <span
          style={{
            position: 'absolute',
            top: -1,
            right: -1,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: colors.stroke,
            border: '1.5px solid rgba(12,6,28,0.9)',
          }}
        />
      )}
    </button>
  );
};

// ── Last 7 Days Dots — ported verbatim from DiyaPrasadCard.jsx ──────────────
const StreakDots = ({ history = [], streak = 0 }) => {
  const dots = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const dateStr = d.toISOString().slice(0, 10);
    const claimed = (history || []).some((h) => h.claim_date === dateStr);
    return { dateStr, claimed };
  });

  return (
    <div className="flex gap-1.5 justify-center mt-2" data-testid="karma-wheel-streak-dots">
      {dots.map(({ dateStr, claimed }) => (
        <div
          key={dateStr}
          title={dateStr}
          className={`w-2.5 h-2.5 rounded-full border transition-all duration-300 ${
            claimed ? 'bg-[color:var(--gold-primary)] border-[color:var(--gold-primary)] shadow-[0_0_6px_rgba(201,168,76,0.5)]' : 'bg-white/5 border-white/10'
          }`}
        />
      ))}
    </div>
  );
};

// ── Countdown hook — ported verbatim ─────────────────────────────────────────
function useCountdown(nextClaimAt) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!nextClaimAt) return;
    const tick = () => {
      const diff = new Date(nextClaimAt) - new Date();
      if (diff <= 0) {
        setLabel('Available now');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setLabel(`${h}h ${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [nextClaimAt]);
  return label;
}

// ── IST today/yesterday helper — ported verbatim from DiyaPrasadCard.jsx ────
const getIstTodayAndYesterday = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istOffset = 5.5 * 3600000;
  const istNow = new Date(utc + istOffset);

  const todayStr = istNow.toISOString().slice(0, 10);

  const yesterday = new Date(istNow);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  return { todayStr, yesterdayStr, istNow };
};

// Re-read the token fresh every time — avoids stale closure or render-time null
const getAuthToken = () =>
  typeof window !== 'undefined'
    ? localStorage.getItem('jyotish_session_token') || localStorage.getItem('token') || sessionStorage.getItem('token') || null
    : null;

// EN/HI copy follows the same `jyotish_report_lang` convention the predictions
// and report surfaces use (PredictionsDashboard.jsx:96-102). Defaults to English.
const resolveLanguage = () => {
  try {
    return localStorage.getItem('jyotish_report_lang') === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
};

// S-1: welcome-back line shown after a claim that restarted a lapsed streak. The
// day count and karma value come from the server response (streak_days +
// next_bonus), never hard-coded.
const streakResetLine = (language, streakDays, nextBonus) => {
  const day = streakDays || 1;
  const toDay = nextBonus?.day;
  const value = nextBonus?.value;
  if (language === 'hi') {
    const tail = (toDay && value) ? ` ${toDay} din mein ${value} karma bonus.` : '';
    return `Wapas swagat hai — nayi streak ka Din ${day}.${tail}`;
  }
  const tail = (toDay && value) ? ` ${toDay} days to your ${value}-karma bonus.` : '';
  return `Welcome back — Day ${day} of a fresh streak.${tail}`;
};

// ── Popover — chip-anchored, replaces ChakraRewardWidget's full-screen modal ─

const KarmaWheelPopover = ({ isOpen, onClose, anchorRef, userInfo, onRewardClaimed, onStateChange }) => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [rewardResult, setRewardResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const language = resolveLanguage();
  const popoverRef = useRef(null);
  const countdown = useCountdown(status?.next_claim_at || rewardResult?.next_claim_at);

  const fetchStatus = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setLoading(false);
      return;
    }
    try {
      const [statusRes, histRes] = await Promise.all([
        fetch(`${getBackendUrl()}/api/rewards/status`, { headers: { Authorization: `Bearer ${authToken}` } }),
        fetch(`${getBackendUrl()}/api/rewards/history?limit=7`, { headers: { Authorization: `Bearer ${authToken}` } }),
      ]);
      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
      }
      if (histRes.ok) {
        const h = await histRes.json();
        setHistory(h.history || []);
      }
    } catch (e) {
      console.error('[KarmaWheel] fetch status error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      // legacy 'diya' event name — kept for analytics continuity; feature is 'Karma Wheel'
      trackEvent('diya_card_viewed', { user_id: userInfo?.user_id });
    }
  }, [isOpen, fetchStatus, userInfo?.user_id]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (event) => {
      const insideAnchor = anchorRef?.current && anchorRef.current.contains(event.target);
      const insidePopover = popoverRef.current && popoverRef.current.contains(event.target);
      if (!insideAnchor && !insidePopover) onClose();
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, anchorRef]);

  // Handle the daily claim. S-1: the claim always succeeds — a missed day no
  // longer prompts a freeze/reset interstitial, so there are no claim options.
  const handleClaimPrasad = async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setClaimError('Session expired. Please sign in again.');
      return;
    }

    setClaiming(true);
    setClaimError(null);
    // legacy 'diya' event name — kept for analytics continuity; feature is 'Karma Wheel'
    trackEvent('diya_claim_tapped', { user_id: userInfo?.user_id });

    try {
      const res = await fetch(`${getBackendUrl()}/api/rewards/daily-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        setClaimError(data.message || data.error || 'Claim failed.');
        setClaiming(false);
        return;
      }

      // Success!
      setRewardResult(data);
      setStatus((prev) => ({
        ...prev,
        claimed_today: true,
        streak_days: data.streak_days,
        next_claim_at: data.next_claim_at,
      }));

      if (onRewardClaimed) onRewardClaimed();

      const histRes = await fetch(`${getBackendUrl()}/api/rewards/history?limit=7`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (histRes.ok) {
        const h = await histRes.json();
        setHistory(h.history || []);
      }
    } catch (e) {
      console.error('[KarmaWheel] claim failed:', e);
      setClaimError('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // State machine derivations — ported verbatim from DiyaPrasadCard.jsx
  const isClaimed = status?.claimed_today || rewardResult?.already_claimed;

  const getAtRiskState = () => {
    if (isClaimed) return false;
    const { yesterdayStr, istNow } = getIstTodayAndYesterday();
    const isAfter20 = istNow.getHours() >= 20;
    const yesterdayClaimed = (history || []).some((h) => h.claim_date === yesterdayStr);
    return isAfter20 && yesterdayClaimed;
  };

  const isAtRisk = getAtRiskState();

  // S-1 removed the BROKEN state — a missed day now auto-restarts the streak on
  // claim and shows a welcome-back line instead of a freeze/reset interstitial.
  let cardState = 'UNCLAIMED';
  if (isClaimed) cardState = 'CLAIMED';
  else if (isAtRisk) cardState = 'AT_RISK';

  // Report the derived state up to the chip so its wheel button reflects it
  // even while the popover is closed.
  useEffect(() => {
    if (onStateChange) onStateChange(cardState);
  }, [cardState, onStateChange]);

  const streakDays = rewardResult?.streak_days || status?.streak_days || 0;
  const discountCode = rewardResult?.discount_code;

  const streakMilestone =
    rewardResult && !rewardResult.already_claimed
      ? streakDays === 7
        ? 'Saptah Siddhi! 7-day streak — 500 bonus Karma awarded'
        : streakDays === 14
        ? 'Paksha Siddhi! 14-day streak — 1000 Karma bonus flows to you'
        : streakDays === 30
        ? 'Maas Siddhi! 30-day devotion streak milestone reached'
        : null
      : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, scale: 0.92, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: -8 }}
          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          className="absolute right-0 top-full mt-2 w-[320px] max-w-[90vw] bg-[rgba(16,8,32,0.98)] border border-[rgba(201,168,76,0.4)] rounded-2xl shadow-[0_0_60px_rgba(201,168,76,0.12)] z-[60] overflow-hidden"
          data-testid="karma-wheel-popover"
        >
          <div className="p-5 flex flex-col items-center">
            <p className="text-[rgba(201,168,76,0.6)] text-[11px] tracking-[3px] mb-1.5">॥ आज का प्रसाद ॥</p>

            {loading ? (
              <div className="py-8">
                <div className="animate-spin w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="w-full">
                {/* State headline */}
                {cardState === 'UNCLAIMED' && (
                  <div className="text-center mb-4">
                    <h3 className="text-white text-base font-bold">Aaj ka Prasad</h3>
                    <p className="text-gray-400 text-xs mt-0.5">Claim your daily cosmic reward to keep your streak alive.</p>
                  </div>
                )}

                {cardState === 'AT_RISK' && (
                  <div className="text-center mb-4">
                    <div className="flex items-center gap-1.5 justify-center text-red-400">
                      <Flame className="w-3.5 h-3.5 animate-bounce" />
                      <p className="text-[10px] font-bold tracking-wider uppercase">Devotion At Risk</p>
                    </div>
                    <h3 className="text-white text-base font-bold mt-0.5">Devotion at Risk!</h3>
                    <p className="text-red-300/80 text-xs mt-0.5">
                      Your streak breaks in <span className="font-semibold text-amber-400">{countdown}</span>. Claim immediately!
                    </p>
                  </div>
                )}

                {cardState === 'CLAIMED' && (
                  <div className="text-center mb-4">
                    <p className="text-emerald-400 text-[10px] font-bold tracking-wider uppercase flex items-center gap-1 justify-center">
                      <Check className="w-3 h-3" /> claimed
                    </p>
                    <h3 className="text-white text-base font-bold mt-0.5">Prasad Claimed</h3>
                    <div className="mt-1 flex items-baseline gap-1.5 justify-center">
                      <span className="text-xl font-extrabold text-[color:var(--gold-primary)]">
                        {rewardResult?.reward_value || status?.active_discount?.discount_percent || status?.active_discount?.code || '✦'}
                      </span>
                      <span className="text-xs text-gray-300">{rewardResult?.reward_label || 'Devotional Reward'}</span>
                    </div>
                    {/* S-1: welcome-back line when this claim restarted a lapsed streak */}
                    {rewardResult?.streak_reset && (
                      <p
                        className="text-emerald-300 text-xs font-medium mt-1.5 leading-relaxed"
                        data-testid="streak-reset-line"
                      >
                        {streakResetLine(language, rewardResult.streak_days, rewardResult.next_bonus)}
                      </p>
                    )}
                    {rewardResult?.blessing_name && (
                      <p className="text-gray-400 text-[11px] italic mt-1.5 leading-relaxed">"{rewardResult.blessing_message}"</p>
                    )}
                    <p className="text-[10px] text-gray-500 tracking-wider mt-3">
                      NEXT PRASAD IN <span className="text-white font-mono font-bold">{countdown || 'Tomorrow'}</span>
                    </p>
                  </div>
                )}

                {claimError && (
                  <div className="w-full bg-red-500/10 border border-red-500/30 rounded-lg px-3.5 py-2.5 mb-3 text-center">
                    <p className="text-red-400 text-xs">{claimError}</p>
                  </div>
                )}

                {/* Action area — state-specific */}
                {cardState === 'UNCLAIMED' && (
                  <button
                    onClick={() => handleClaimPrasad()}
                    disabled={claiming}
                    data-testid="accept-prasad-btn"
                    className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-[var(--gold-primary)] to-[#E8C96A] text-[#0A0516] hover:from-[#E8C96A] hover:to-[var(--gold-primary)] transition shadow-[0_4px_15px_rgba(201,168,76,0.25)] disabled:opacity-60"
                  >
                    {claiming ? 'Claiming...' : 'Claim Prasad ✦'}
                  </button>
                )}

                {cardState === 'AT_RISK' && (
                  <button
                    onClick={() => handleClaimPrasad()}
                    disabled={claiming}
                    data-testid="accept-prasad-btn"
                    className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-orange-500 hover:to-red-500 transition shadow-[0_4px_15px_rgba(239,68,68,0.3)] disabled:opacity-60 animate-pulse"
                  >
                    {claiming ? 'Claiming...' : 'Claim Prasad ✦'}
                  </button>
                )}

                {/* Discount code, if this claim won one */}
                {discountCode && (
                  <div className="w-full bg-[rgba(201,168,76,0.05)] border border-[rgba(201,168,76,0.2)] rounded-xl px-4 py-3 mt-3 text-center">
                    <p className="text-[color:var(--gold-primary)] text-[11px] mb-1.5">Your Discount Code</p>
                    <p className="text-white font-bold text-[13px] tracking-wide mb-2">{discountCode}</p>
                    <button
                      onClick={() => copyCode(discountCode)}
                      className="bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.3)] rounded-lg px-4 py-1.5 text-[color:var(--gold-primary)] text-xs"
                    >
                      {copied ? 'Copied!' : 'Copy Code'}
                    </button>
                  </div>
                )}

                {/* Streak milestone banner */}
                {streakMilestone && (
                  <div className="bg-[rgba(250,204,21,0.08)] border border-[rgba(250,204,21,0.25)] rounded-xl px-3.5 py-2.5 mt-3 text-center">
                    <p className="text-[#FACC15] text-xs font-semibold">🙏 {streakMilestone}</p>
                  </div>
                )}

                {/* Streak count + 7-day dots */}
                <div className="text-center mt-3.5">
                  <span className="text-[9px] text-gray-500 tracking-wider block">CURRENT STREAK</span>
                  <span className="text-white text-xs font-bold font-mono block">
                    {status?.streak_days || 0} {status?.streak_days === 1 ? 'Day' : 'Days'} 🔥
                  </span>
                  <StreakDots history={history} streak={status?.streak_days || 0} />
                </div>
              </div>
            )}

            <button
              onClick={onClose}
              className="text-white/30 text-xs mt-4"
              data-testid="karma-wheel-popover-close"
            >
              Close
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ── Main export: KarmaWheel ──────────────────────────────────────────────────
// Compact header chip + spin popover. Renders identically on desktop and
// mobile since it lives in MobileLayout.jsx's shared, width-responsive header.
export default function KarmaWheel({ userInfo, onRewardClaimed }) {
  const [open, setOpen] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [wheelState, setWheelState] = useState('UNCLAIMED');
  const anchorRef = useRef(null);

  const authToken =
    typeof window !== 'undefined'
      ? localStorage.getItem('jyotish_session_token') || localStorage.getItem('token') || sessionStorage.getItem('token')
      : null;

  useEffect(() => {
    // Quick status check on mount so the chip's pulse/glow is correct before
    // the popover is ever opened.
    if (!authToken) return;
    fetch(`${getBackendUrl()}/api/rewards/status`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setClaimed(data.claimed_today);
      })
      .catch(() => {});
  }, [authToken]);

  const handleRewardClaimed = useCallback(() => {
    setClaimed(true);
    if (onRewardClaimed) onRewardClaimed();
  }, [onRewardClaimed]);

  // Wheel visual variant follows the popover's derived state machine.
  // (S-1 removed the BROKEN state, so the wheel is only default / atRisk now.)
  const wheelVariant = wheelState === 'AT_RISK' ? 'atRisk' : 'default';
  const wheelClaimed = claimed && wheelState !== 'AT_RISK';

  return (
    <div className="relative" ref={anchorRef}>
      <WheelOfTimes claimed={wheelClaimed} variant={wheelVariant} onClick={() => setOpen((o) => !o)} size={36} />
      <KarmaWheelPopover
        isOpen={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        userInfo={userInfo}
        onRewardClaimed={handleRewardClaimed}
        onStateChange={setWheelState}
      />
    </div>
  );
}
